import LearnLayout, { LEARN_PAGES } from './LearnLayout'

const page = LEARN_PAGES.find((p) => p.slug === 'treatment')!

export default function TreatmentPage() {
  return (
    <LearnLayout page={page}>
      <p>
        There is no single cure-all for endometriosis or adenomyosis, and anyone who tells you
        otherwise is selling something. What there is, is a toolbox. The right plan layers a few of
        these tools together, tuned to your pain, your fertility goals, and where you are in life.
        Here is an honest map of what each one does.
      </p>

      <h2 id="hormonal">Hormonal management</h2>
      <p>
        The first line for most people. Because both diseases are driven by estrogen and by
        cyclical bleeding, calming the hormonal cycle calms the symptoms. <strong>Combined
        hormonal contraceptives</strong> and <strong>progestins</strong> (including the hormonal
        IUD) suppress ovulation and thin the lining. <strong>GnRH analogues</strong> go further,
        pushing the body into a temporary low-estrogen state.
      </p>
      <p>
        What hormones <em>can</em> do is meaningfully reduce pain and bleeding for many women. What
        they <em>can&rsquo;t</em> do is cure the disease. Remember progesterone resistance,
        where the lesions stop fully responding to the very hormones meant to control them. Hormonal
        therapy manages the fire; it doesn&rsquo;t remove the fuel.
      </p>

      <h2 id="pain-and-pt">Pain care &amp; pelvic floor physical therapy</h2>
      <p>
        Because chronic pelvic pain rewires the nervous system through{' '}
        <strong>central sensitization</strong>, treating the pain itself is real medicine, not a
        consolation prize. <strong>Pelvic floor physical therapy</strong> addresses the muscle
        guarding and dysfunction that builds up over years of pain, and for many women it is one of
        the most underrated, highest-impact things they ever try.
      </p>
      <p>
        Anti-inflammatories, nerve-targeted medications, and a care team that takes the pain
        seriously round this out. None of it removes lesions, but it can change how you live day to
        day, and it works alongside every other option here.
      </p>

      <h2 id="surgery">Surgery</h2>
      <p>
        When pain is severe, fertility is on the line, or other approaches have stalled,{' '}
        <strong>excision surgery</strong> is the gold standard for endometriosis. It is the only
        treatment that removes the disease at the root rather than managing its symptoms. For
        adenomyosis, which lives inside the uterine muscle, the definitive surgical answer is
        hysterectomy, which is why timing and fertility planning matter so much for younger
        patients.
      </p>
      <p>
        Surgery isn&rsquo;t the start of the journey for everyone, and it isn&rsquo;t a standalone
        fix; it works best as part of a plan. But for the disease itself, nothing else
        removes it.{' '}
        <em>We cover this in depth on the excision surgery page.</em>
      </p>

      <h2 id="lifestyle">Lifestyle &amp; the things in your control</h2>
      <p>
        Diet, sleep, stress, and movement won&rsquo;t cure endometriosis (and it is harmful
        to suggest they will), but they genuinely influence the inflammatory environment and
        how much pain you carry. The honest framing: these are levers that can take the edge off and
        support every other treatment, not replacements for them.
      </p>
      <p>
        This is also where tracking earns its keep. When you log symptoms day after day, you start
        to see <strong>your</strong> patterns: what reliably triggers a flare, what helps,
        which treatments are actually working. That record turns a vague &ldquo;I think it&rsquo;s a
        bit better&rdquo; into evidence you and your doctor can act on.
      </p>

      <div className="learn-bottom-line">
        <span className="learn-bottom-line-label">The bottom line</span>
        <p>
          Treatment is a plan, not a pill. Most women do best with a layered approach:
          hormonal management to calm the cycle, pelvic floor and pain care to treat the nervous
          system, excision surgery when the disease itself needs to come out, and lifestyle levers
          supporting all of it. The right combination depends on your pain, your goals, and your
          timeline. The point of Pelvia is to make that plan, and the evidence behind it,{' '}
          <strong>yours</strong>, built in days rather than after a decade of waiting.
        </p>
      </div>
    </LearnLayout>
  )
}
